const BASE_URL = process.env.BUN_PUBLIC_API_BASE_URL;

export interface ProfileCard {
  profile_card_id: string;
  name: string;
  img_url: string | null;
  description: string;
  is_active: boolean;
  created_at: string;
}

export interface CreateCardResult {
  card_id: string;
  img_url?: string;
}

export interface CreateCardPayload {
  name: string;
  description?: string;
  img_content: string;
}

export interface UpdateCardPayload {
  name: string;
  description: string;
  is_active: boolean;
}

class CardsProvider {
  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const text = await res.text().catch(() => "Unknown error");
      throw new Error(text || `HTTP ${res.status}`);
    }

    const text = await res.text();
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  // Admin endpoints
  listAll(): Promise<ProfileCard[]> {
    return this.request<ProfileCard[]>("/v1/admin/cards");
  }

  create(payload: CreateCardPayload): Promise<CreateCardResult> {
    return this.request<CreateCardResult>("/v1/admin/cards", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  uploadImage(cardId: string, base64Content: string): Promise<void> {
    return this.request<void>(`/v1/admin/cards/${encodeURIComponent(cardId)}/image`, {
      method: "POST",
      body: JSON.stringify({ content: base64Content }),
    });
  }

  update(cardId: string, payload: UpdateCardPayload): Promise<void> {
    return this.request<void>(`/v1/admin/cards/${encodeURIComponent(cardId)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  delete(cardId: string): Promise<void> {
    return this.request<void>(`/v1/admin/cards/${encodeURIComponent(cardId)}`, {
      method: "DELETE",
    });
  }

  getCardsForUser(userId: string): Promise<ProfileCard[]> {
    return this.request<ProfileCard[]>(`/v1/admin/cards/user/${encodeURIComponent(userId)}`);
  }

  grantToUser(cardId: string, userId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/cards/${encodeURIComponent(cardId)}/grant/${encodeURIComponent(userId)}`,
      { method: "POST" }
    );
  }

  revokeFromUser(cardId: string, userId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/cards/${encodeURIComponent(cardId)}/revoke/${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    );
  }
}

export const cardsProvider = new CardsProvider();
